/**
 * CapacityBoundsRow — the Min and Max controls of one capacity rule.
 *
 * Both are opt-out: Max defaults to a number with an "Ignore" escape (∞), Min
 * defaults to OFF because a floor changes how the rotation behaves the moment
 * it exists, and nobody should acquire one by creating a rule. "Fill up"
 * (opt-in, needs a numeric Max) turns the max into a target: the rotation
 * promotes from the queue until the pool sits at its cap.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ArrowUpToLine, Infinity as InfinityIcon } from 'lucide-react';
import type { CapacityRule, CapacitySlot } from '@trafficpulse/shared';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface CapacityBoundsRowProps {
  rule: CapacityRule;
  slot: CapacitySlot;
  onPatch: (next: Partial<CapacityRule>) => void;
}

const TOGGLE =
  'inline-flex h-6 items-center gap-1 rounded border px-1.5 text-[10.5px] font-medium transition-colors';
const TOGGLE_ON = 'border-primary bg-primary/10 text-primary';
const TOGGLE_OFF = 'border-border text-muted-foreground hover:text-foreground';
const FIELD =
  'h-6 w-12 rounded border border-input bg-background px-1 text-right text-[11px] tabular-nums focus:outline-none focus:ring-1 focus:ring-ring';
const CAPTION =
  'text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground';

const CapacityBoundsRow: FC<CapacityBoundsRowProps> = ({ rule, slot, onPatch }) => {
  const perRule = slot === 'paths-rules';
  const ignoreMax = rule.max === null;
  const noMin = typeof rule.min !== 'number';
  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        <span className={CAPTION}>{perRule ? 'Min per rule' : 'Min'}</span>
        {noMin ? (
          <span className="text-[11px] italic text-muted-foreground">none</span>
        ) : (
          <NumberField
            value={rule.min ?? 0}
            min={0}
            max={999}
            onChange={(v) => onPatch({ min: v })}
            aria-label="Min active"
            className={FIELD}
          />
        )}
        <button
          type="button"
          onClick={() => onPatch({ min: noMin ? 1 : null })}
          aria-pressed={noMin}
          title={
            noMin
              ? 'No minimum — the rotation may empty this pool'
              : 'Remove the minimum'
          }
          className={cn(TOGGLE, noMin ? TOGGLE_ON : TOGGLE_OFF)}
        >
          No min
        </button>
      </div>
      <div className="inline-flex items-center gap-1.5">
        <span className={CAPTION}>{perRule ? 'Max per rule' : 'Max'}</span>
        {ignoreMax ? (
          <span className="text-[11px] italic text-muted-foreground">unlimited</span>
        ) : (
          <NumberField
            value={rule.max ?? 0}
            min={0}
            max={999}
            onChange={(v) => onPatch({ max: v })}
            aria-label="Max active"
            className={FIELD}
          />
        )}
        <button
          type="button"
          onClick={() => onPatch({ max: ignoreMax ? 5 : null })}
          aria-pressed={ignoreMax}
          title="Ignore — no cap on this rule (unlimited)"
          className={cn(TOGGLE, ignoreMax ? TOGGLE_ON : TOGGLE_OFF)}
        >
          <InfinityIcon className="h-3 w-3" aria-hidden />
          Ignore
        </button>
        {!ignoreMax ? (
          <button
            type="button"
            onClick={() => onPatch({ fillToMax: rule.fillToMax ? undefined : true })}
            aria-pressed={Boolean(rule.fillToMax)}
            title={
              rule.fillToMax
                ? 'Filling up — the rotation promotes from the queue until live count reaches the max'
                : 'Fill up — promote from the queue until live count reaches the max'
            }
            className={cn(TOGGLE, rule.fillToMax ? TOGGLE_ON : TOGGLE_OFF)}
          >
            <ArrowUpToLine className="h-3 w-3" aria-hidden />
            Fill up
          </button>
        ) : null}
      </div>
    </>
  );
};

export default CapacityBoundsRow;
